import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Lightbulb, HelpCircle, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const REFLECTIONS_KEY = "mwq_reflections";

const CHOICES = [
  { id: "loved",   label: "Loved it",         emoji: "💛", Icon: Heart,      color: "bg-rose-100 border-rose-300 text-rose-700"     },
  { id: "learned", label: "Learned something", emoji: "💡", Icon: Lightbulb,  color: "bg-amber-100 border-amber-300 text-amber-800"  },
  { id: "tricky",  label: "A bit tricky",     emoji: "🤔", Icon: HelpCircle, color: "bg-sky-100 border-sky-300 text-sky-800"        },
  { id: "more",    label: "Want more like this", emoji: "✨", Icon: Sparkles, color: "bg-violet-100 border-violet-300 text-violet-800" },
];

/**
 * <ReflectionPanel /> — one-tap post-lesson reflection.
 *
 * Shown at the end of every lesson (below the EndPanel). Kids tap
 * one of four warm feelings, optionally write a short note about
 * what they enjoyed, and the reflection is saved locally + posted
 * to `/api/reflections` for the teacher dashboard.
 *
 * Metacognition research (Zimmerman 2002; Hattie 2012) shows a
 * 30-second self-explanation after learning locks in the material
 * significantly better than another minute of content — hence the
 * choice to place this at the natural pause point.
 */
export default function ReflectionPanel({ code, outcome, subject, ageTier }) {
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // If the child has already reflected on this lesson today, hide
    // the prompt so it doesn't nag on re-visits.
    try {
      const list = JSON.parse(localStorage.getItem(REFLECTIONS_KEY) || "[]");
      const today = new Date().toISOString().slice(0, 10);
      if (list.some((r) => r.code === code && r.date === today)) setSaved(true);
    } catch { /* noop */ }
  }, [code]);

  const submit = async () => {
    if (!choice) return;
    const entry = {
      code, outcome, subject, ageTier,
      choice, note: note.trim().slice(0, 300),
      date: new Date().toISOString().slice(0, 10),
      created_at: new Date().toISOString(),
    };
    try {
      const list = JSON.parse(localStorage.getItem(REFLECTIONS_KEY) || "[]");
      localStorage.setItem(REFLECTIONS_KEY, JSON.stringify([entry, ...list].slice(0, 200)));
    } catch { /* noop */ }
    setSaved(true);
    toast.success("Reflection saved. Nice thinking! 🌟");
    // Best-effort server post — a failure never blocks the local save.
    try {
      await fetch(`${API}/api/reflections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
      });
    } catch { /* silent */ }
  };

  if (saved) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-4 flex items-center gap-3"
        data-testid="reflection-saved"
      >
        <CheckCircle2 size={22} className="text-emerald-700 shrink-0" />
        <p className="text-sm font-semibold text-emerald-800">Reflection saved. See you tomorrow for a new adventure!</p>
      </motion.div>
    );
  }

  return (
    <section
      className="mt-6 rounded-3xl border-4 border-slate-900 bg-white shadow-[6px_6px_0_0_rgba(15,23,42,1)] p-5"
      data-testid="reflection-panel"
      aria-label="How did this lesson go?"
    >
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Reflection</p>
      <h3 className="mt-1 font-heading text-xl md:text-2xl font-bold text-slate-900">How did this lesson go?</h3>
      <p className="mt-1 text-sm text-slate-600">Pick one and tell us what you enjoyed — this helps your brain remember.</p>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" role="radiogroup" aria-label="How did this lesson go?">
        {CHOICES.map((c) => {
          const on = choice === c.id;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setChoice(c.id)}
              data-testid={`reflection-${c.id}`}
              className={`inline-flex flex-col items-center gap-1 py-3 rounded-2xl border-[3px] font-bold text-xs transition-all shadow-[0_3px_0_0_rgba(15,23,42,1)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-yellow-300 ${
                on ? "bg-slate-900 border-slate-900 text-white -translate-y-0.5" : `${c.color}`
              }`}
            >
              <span className="text-2xl" aria-hidden>{c.emoji}</span>
              {c.label}
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {choice && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
          >
            <label htmlFor="reflection-note" className="mt-4 block text-[10px] font-black uppercase tracking-widest text-slate-500">
              Optional — what did you enjoy?
            </label>
            <textarea
              id="reflection-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="One thing you found interesting…"
              data-testid="reflection-note"
              className="mt-1 w-full rounded-xl border-2 border-slate-300 px-3 py-2 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
            <button
              type="button"
              onClick={submit}
              data-testid="reflection-submit"
              className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full border-[3px] border-slate-900 bg-emerald-400 hover:bg-emerald-500 font-bold text-sm shadow-[0_4px_0_0_rgba(15,23,42,1)]"
            >
              Save reflection
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
